// The full stop in "Score." belongs to the logo, not to sentences. Running copy
// is therefore "Score" / "Score's" and the capital S carries the brand; the
// dotted logomark is reserved for places a logo could physically sit — titles,
// hero lines and standalone brand labels. See STANDALONE_BRAND_MOMENTS.

// Whole-message matches (compared case-insensitively against the entire value)
// where the message *is* a label, title or hero line rather than a sentence, so
// the dotted logomark could stand in its place. The same wording inside a
// sentence falls through to the generic rules and rebrands without the dot.
const STANDALONE_BRAND_MOMENTS: Record<string, string> = {
  acre: 'Score.',
  'welcome to acre': 'Welcome to Score.',
  'acre surveying': 'Score. Surveying',
  'acre global mi download': 'Score. Global MI Download',
}

// Phrases the generic brand substitution must not touch: legal-entity/document
// names (the Acre Support entities keep their name — title-case "Support"
// only), and land-measurement usages of "acre". A phrase with an entry in
// PHRASE_REWRITES is replaced by that exact text (which is itself exempt from
// the generic rules); the rest are carried through verbatim.
const PROTECTED_PHRASES = [
  'Acre Platforms Limited',
  'Acre Platforms Ltd',
  'Acre Acceptable Use Policy',
  'Acre Software Licence Agreement',
  'Acre Software',
  'Acre Support Ltd',
  'Acre Support UK',
  'Acre Support',
  'one acre (0.404ha)',
  'one acre',
].sort((a, b) => b.length - a.length)

// Legal entity names are never contracted: always "Limited", never "Ltd" — the
// contracted form also normalises to the full legal name so it can't degrade
// into "Score Platforms Ltd" via the generic substitution. The trading name is
// running copy here (it sits mid-sentence, ahead of a comma), so it takes no
// dot. "Acre Software" is the product, and rebrands to the bare brand name
// rather than "Score Software".
const PHRASE_REWRITES: Record<string, string> = {
  'acre platforms limited': 'Acre Platforms Limited trading as Score',
  'acre platforms ltd': 'Acre Platforms Limited trading as Score',
  'acre software': 'Score',
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const PROTECTED_PATTERN = new RegExp(PROTECTED_PHRASES.map(escapeRegExp).join('|'), 'gi')

// The support entity is only the title-case "Support" form; lowercase "support"
// refers to the company's support function, so the mention rebrands as usual
// ("contact Acre support" → "contact Score support").
const SUPPORT_ENTITY_PREFIX = /^acre support/i

const POSSESSIVE_PATTERN = /\b[Aa]cre's\b/g
const WHOLE_WORD_PATTERN = /\b[Aa]cre\b/g

const brandSegment = (segment: string): string =>
  segment.replace(POSSESSIVE_PATTERN, "Score's").replace(WHOLE_WORD_PATTERN, 'Score')

const resolveProtectedMatch = (matched: string): string => {
  const rewrite = PHRASE_REWRITES[matched.toLowerCase()]
  if (rewrite) {
    return rewrite
  }
  if (SUPPORT_ENTITY_PREFIX.test(matched) && !matched.includes('Support')) {
    return brandSegment(matched)
  }
  return matched
}

// A whole-value match against STANDALONE_BRAND_MOMENTS wins outright — those are
// the only strings that keep the logo's full stop. Everything else splits on the
// protected phrases so they're carried through verbatim (or swapped for their
// rewrite), and only the text in between takes the brand substitution.
// For strings that don't come through the i18n message map — e.g. quote notes
// returned by the conveyancing API and rendered verbatim.
export const applyScoreBrandingToText = (value: string): string => {
  const standalone = STANDALONE_BRAND_MOMENTS[value.toLowerCase()]
  if (standalone) {
    return standalone
  }

  const segments: string[] = []
  let lastIndex = 0

  PROTECTED_PATTERN.lastIndex = 0
  let match = PROTECTED_PATTERN.exec(value)

  while (match !== null) {
    segments.push(brandSegment(value.slice(lastIndex, match.index)))
    segments.push(resolveProtectedMatch(match[0]))
    lastIndex = match.index + match[0].length
    match = PROTECTED_PATTERN.exec(value)
  }

  segments.push(brandSegment(value.slice(lastIndex)))

  return segments.join('')
}

export const applyScoreBranding = (messages: Record<string, string>): Record<string, string> => {
  const result: Record<string, string> = {}

  Object.entries(messages).forEach(([key, value]) => {
    result[key] = typeof value === 'string' ? applyScoreBrandingToText(value) : value
  })

  return result
}
